/**
 * BloqueWorkspace — Paso 2: dibujar bloques + lanzar motor V5
 *
 * Abre full-screen al agregar/analizar bloques de un lote.
 * Recibe el lote (con su geojson y metadatos) y permite:
 *   1. Cargar el GeoTIFF del dron
 *   2. Dibujar polígonos de bloque (B1, B2...) sobre la imagen
 *   3. Lanzar Motor V5 y ver resultados
 *   4. Guardar en emp_bloques
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { fromBlob } from 'geotiff';
import proj4 from 'proj4';
import {
  ArrowLeft, Upload, ZoomIn, ZoomOut, RotateCcw, Plus, X,
  FlaskConical, Loader2, AlertCircle, CheckCircle2, Download,
  ImagePlus, Layers,
} from 'lucide-react';
import { supabase } from '../../supabase';
import GeoMapPreview from '../../components/GeoMapPreview';
import { invocarMotorPythonBloquesPoligonoArchivo, invocarAutoDetectarBloques, type MotorPythonBatchResponse } from './deteccionService';
import type { EmpFinca, EmpLote } from '../../types/empacadora';
import { inputCls } from '../../components/ui';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface BloqueWorkspaceProps {
  finca: EmpFinca;
  lote: EmpLote;
  empresaId: number;
  bloqueResaltado?: number | null;
  onClose: () => void;
  onSaved?: () => void;
}

// ─── GeoTIFF helpers ─────────────────────────────────────────────────────────

function inferEpsg(geoKeys: any): number {
  const proj = Number(geoKeys?.ProjectedCSTypeGeoKey || 0);
  const geog = Number(geoKeys?.GeographicTypeGeoKey || 0);
  if (proj > 0) return proj;
  if (geog > 0) return geog;
  const m = String(geoKeys?.GTCitationGeoKey || '').match(/UTM\s*zone\s*(\d{1,2})\s*([NS])/i);
  if (m) { const z = Number(m[1]); return m[2].toUpperCase() === 'S' ? 32700 + z : 32600 + z; }
  return 0;
}

function toWgs84(epsg: number, x: number, y: number): [number, number] {
  if (epsg === 4326) return [x, y];
  let from = '';
  if (epsg >= 32601 && epsg <= 32660) from = `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`;
  else if (epsg >= 32701 && epsg <= 32760) from = `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`;
  else throw new Error(`CRS EPSG:${epsg} no soportado. Exportá el GeoTIFF en WGS84 o UTM.`);
  const [lng, lat] = proj4(from, 'WGS84', [x, y]);
  return [lng, lat];
}

async function geotiffToPng(file: File): Promise<{ url: string; bounds: [[number, number], [number, number]] }> {
  const tiff = await fromBlob(file);
  const img = await tiff.getImage();
  const geoKeys = img.getGeoKeys();
  const epsg = inferEpsg(geoKeys);
  if (!epsg) throw new Error('El GeoTIFF no tiene información de proyección. Exportalo con CRS.');

  const bbox = img.getBoundingBox();
  const [minLng, minLat] = toWgs84(epsg, bbox[0], bbox[1]);
  const [maxLng, maxLat] = toWgs84(epsg, bbox[2], bbox[3]);

  const MAX_DIM = 3000;
  const srcW = img.getWidth();
  const srcH = img.getHeight();
  const scale = Math.min(1, MAX_DIM / Math.max(srcW, srcH));
  const outW = Math.round(srcW * scale);
  const outH = Math.round(srcH * scale);

  const rasters = await img.readRasters({ width: outW, height: outH, interleave: true });
  const data = rasters as unknown as Uint8Array;
  const spp = img.getSamplesPerPixel();

  const canvas = document.createElement('canvas');
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext('2d')!;
  const imgData = ctx.createImageData(outW, outH);

  for (let i = 0; i < outW * outH; i++) {
    if (spp >= 3) {
      imgData.data[i * 4 + 0] = Math.min(255, Math.round(Number(data[i * spp + 0])));
      imgData.data[i * 4 + 1] = Math.min(255, Math.round(Number(data[i * spp + 1])));
      imgData.data[i * 4 + 2] = Math.min(255, Math.round(Number(data[i * spp + 2])));
      imgData.data[i * 4 + 3] = spp >= 4 ? Math.min(255, Math.round(Number(data[i * spp + 3]))) : 255;
    } else {
      const v = Math.min(255, Math.round(Number(data[i])));
      imgData.data[i * 4 + 0] = v; imgData.data[i * 4 + 1] = v;
      imgData.data[i * 4 + 2] = v; imgData.data[i * 4 + 3] = 255;
    }
  }
  ctx.putImageData(imgData, 0, 0);
  return { url: canvas.toDataURL('image/png'), bounds: [[minLat, minLng], [maxLat, maxLng]] };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

type BloqueDraw = { bloque_num: number; puntos: [number, number][] };

// Douglas-Peucker simplification
function perpendicularDist(p: [number,number], a: [number,number], b: [number,number]): number {
  const dx = b[0] - a[0]; const dy = b[1] - a[1];
  if (dx === 0 && dy === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy);
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}
function douglasPeucker(pts: [number,number][], epsilon: number): [number,number][] {
  if (pts.length <= 2) return pts;
  let maxD = 0; let idx = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const d = perpendicularDist(pts[i], pts[0], pts[pts.length - 1]);
    if (d > maxD) { maxD = d; idx = i; }
  }
  if (maxD > epsilon) {
    const l = douglasPeucker(pts.slice(0, idx + 1), epsilon);
    const r = douglasPeucker(pts.slice(idx), epsilon);
    return [...l.slice(0, -1), ...r];
  }
  return [pts[0], pts[pts.length - 1]];
}

function fmtNum(v?: number | null, dec = 0) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function kpiColor(kpi?: number) {
  if (kpi == null) return 'var(--ink-faint)';
  if (kpi >= 90) return 'var(--tone-success-text)';
  if (kpi >= 70) return 'var(--tone-warn-text)';
  return '#f87171';
}

// Convierte puntos normalizados (0..1) → GeoJSON Polygon WGS84 usando los bounds del lote
function puntosToGeojson(
  puntos: [number, number][],
  bounds: [[number, number], [number, number]] // [[minLat,minLng],[maxLat,maxLng]]
): object | null {
  if (puntos.length < 3) return null;
  const [[minLat, minLng], [maxLat, maxLng]] = bounds;
  const coords = puntos.map(([nx, ny]) => [
    minLng + nx * (maxLng - minLng),
    maxLat - ny * (maxLat - minLat), // Y invertida: ny=0 → maxLat (arriba)
  ]);
  // cerrar anillo
  coords.push(coords[0]);
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] }, properties: {} };
}

// Convierte GeoJSON Polygon WGS84 → puntos normalizados (0..1) usando los bounds del lote
function geojsonToPuntos(
  geojson: any,
  bounds: [[number, number], [number, number]]
): [number, number][] | null {
  try {
    let geom = geojson?.type === 'Feature' ? geojson.geometry : geojson;
    if (!geom) return null;
    let ring: number[][] = [];
    if (geom.type === 'Polygon') ring = geom.coordinates[0];
    else if (geom.type === 'MultiPolygon') ring = geom.coordinates[0][0];
    else return null;
    const [[minLat, minLng], [maxLat, maxLng]] = bounds;
    const pts: [number, number][] = ring.map(([lng, lat]) => [
      (lng - minLng) / Math.max(1e-10, maxLng - minLng),
      1 - (lat - minLat) / Math.max(1e-10, maxLat - minLat),
    ]);
    // quitar último si cierra el anillo
    if (pts.length > 1) {
      const [fx, fy] = pts[0];
      const [lx, ly] = pts[pts.length - 1];
      if (Math.abs(fx - lx) < 1e-6 && Math.abs(fy - ly) < 1e-6) pts.pop();
    }
    return pts.length >= 3 ? pts : null;
  } catch { return null; }
}

// ─── Contenido ───────────────────────────────────────────────────────────────

function Workspace({ finca, lote, empresaId, bloqueResaltado, onClose, onSaved }: BloqueWorkspaceProps) {
  // Imagen — carga desde Storage si el lote ya tiene raster_png_url
  const [previewUrl, setPreviewUrl] = useState<string>(lote.raster_png_url ?? '');
  const [tifFile, setTifFile] = useState<File | null>(null);
  const [loadingImg, setLoadingImg] = useState(false);
  const [imgErr, setImgErr] = useState('');
  const tifInputRef = useRef<HTMLInputElement>(null);

  // Dibujo bloques — carga polígonos existentes desde emp_bloques
  const [points, setPoints] = useState<[number, number][]>([]);
  const [blocks, setBlocks] = useState<BloqueDraw[]>([]);
  const [zoom, setZoom] = useState(1);

  const [maxNumBD, setMaxNumBD] = useState(0);
  const [bloqueActivo, setBloqueActivo] = useState<number | null>(bloqueResaltado ?? null);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);

  // Edición de vértices
  const [modoEditarVertices, setModoEditarVertices] = useState(false);
  const vertexDragRef = useRef<{ idx: number; startX: number; startY: number } | null>(null);

  // Modo regla
  const [modoRegla, setModoRegla] = useState(false);
  const [reglaPoints, setReglaPoints] = useState<[number, number][]>([]);
  const [reglaDistReal, setReglaDistReal] = useState('');  // para calibrar GSD

  function reglaDistM(a: [number,number], b: [number,number]): number {
    if (!imgDims) return 0;
    const gsdVal = Math.max(0.01, Number(gsd) || 0.05);
    const dx = (b[0] - a[0]) * imgDims.w;
    const dy = (b[1] - a[1]) * imgDims.h;
    return Math.sqrt(dx * dx + dy * dy) * gsdVal;
  }

  function reglaTotalM(): number {
    let total = 0;
    for (let i = 0; i < reglaPoints.length - 1; i++) total += reglaDistM(reglaPoints[i], reglaPoints[i+1]);
    return total;
  }

  function calcAreaM2(puntos: [number, number][]): number | null {
    if (!imgDims || puntos.length < 3) return null;
    const gsdVal = Math.max(0.01, Number(gsd) || 0.05);
    // Shoelace sobre coordenadas en píxeles reales
    let area = 0;
    for (let i = 0; i < puntos.length; i++) {
      const [x1, y1] = puntos[i];
      const [x2, y2] = puntos[(i + 1) % puntos.length];
      area += (x1 * imgDims.w) * (y2 * imgDims.h) - (x2 * imgDims.w) * (y1 * imgDims.h);
    }
    const px2 = Math.abs(area) / 2;
    return Math.round(px2 * gsdVal * gsdVal);
  }

  useEffect(() => {
    if (!lote.id) return;
    supabase.from('emp_bloques')
      .select('num, geojson, conteo_resultado_payload')
      .eq('lote_id', lote.id)
      .order('num')
      .then(({ data }) => {
        if (!data?.length) return;
        const max = Math.max(...data.map(b => b.num ?? 0));
        setMaxNumBD(max);
        const bounds = lote.raster_bounds as [[number,number],[number,number]] | null;
        const loaded: BloqueDraw[] = data
          .map(b => {
            // 1. puntos del motor (coordenadas normalizadas de imagen)
            const puntos: [number, number][] | undefined = b.conteo_resultado_payload?.puntos;
            if (puntos?.length) return { bloque_num: b.num, puntos };
            // 2. geojson WGS84 del conteo calibrado → convertir si hay bounds
            if (b.geojson && bounds) {
              const pts = geojsonToPuntos(b.geojson, bounds);
              if (pts) return { bloque_num: b.num, puntos: pts };
            }
            return null;
          })
          .filter(Boolean) as BloqueDraw[];
        if (loaded.length) setBlocks(loaded);
      });
  }, [lote.id]);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const movedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Motor
  const [gsd, setGsd] = useState('0.05');
  const [camas, setCamas] = useState('12');
  const [running, setRunning] = useState(false);
  const [motorErr, setMotorErr] = useState('');
  const [resultado, setResultado] = useState<MotorPythonBatchResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  // Puntos de plantas detectadas por bloque: bloque_num → puntos normalizados
  const [puntosPlantasPorBloque, setPuntosPlantasPorBloque] = useState<Record<number, [number,number][]>>({});
  const [showPuntos, setShowPuntos] = useState(true);

  const nextBlock = useMemo(() => {
    const maxLocal = blocks.length > 0 ? Math.max(...blocks.map(b => b.bloque_num)) : 0;
    return Math.max(maxLocal, maxNumBD) + 1;
  }, [blocks, maxNumBD]);

  const [bloqueNumInput, setBloqueNumInput] = useState<string>('');
  const bloqueNumEfectivo = bloqueNumInput.trim() !== '' ? parseInt(bloqueNumInput) : nextBlock;

  // Teclado: Backspace/Ctrl+Z deshace punto
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault(); setPoints(p => p.slice(0, -1));
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); setPoints(p => p.slice(0, -1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function cargarTif(file: File) {
    setLoadingImg(true); setImgErr('');
    try {
      const isTiff = /\.(tif|tiff)$/i.test(file.name);
      if (isTiff) {
        const { url } = await geotiffToPng(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(URL.createObjectURL(file));
      }
      setTifFile(file);
    } catch (e: any) {
      setImgErr(e?.message ?? 'Error al leer el archivo.');
    }
    setLoadingImg(false);
  }

  // Pan (solo cuando zoom > 1 y no hay drag de vértice activo)
  function onPanStart(e: React.MouseEvent<HTMLDivElement>) {
    movedRef.current = false;
    // En modo editar vértices: detectar si el click está cerca de un vértice
    if (modoEditarVertices && bloqueActivo != null && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = (e.clientY - rect.top) / rect.height;
      const bloque = blocks.find(b => b.bloque_num === bloqueActivo);
      if (bloque) {
        // Umbral: 8px en coordenadas de pantalla → normalizado
        const threshX = 8 / rect.width;
        const threshY = 8 / rect.height;
        const hit = bloque.puntos.findIndex(([vx, vy]) =>
          Math.abs(vx - nx) < threshX && Math.abs(vy - ny) < threshY
        );
        if (hit >= 0) {
          e.preventDefault();
          e.stopPropagation();
          vertexDragRef.current = { idx: hit, startX: e.clientX, startY: e.clientY };
          return;
        }
      }
      // Clic en fondo en modo editar → no hacer pan ni añadir punto
      return;
    }
    if (vertexDragRef.current != null) return;
    if (zoom <= 1 || !containerRef.current) return;
    e.preventDefault();
    panRef.current = { x: e.clientX, y: e.clientY, sl: containerRef.current.scrollLeft, st: containerRef.current.scrollTop };
    setIsPanning(true);
  }
  function onPanMove(e: React.MouseEvent<HTMLDivElement>) {
    // Drag de vértice
    if (vertexDragRef.current != null && bloqueActivo != null && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      const { idx } = vertexDragRef.current;
      setBlocks(prev => prev.map(b => {
        if (b.bloque_num !== bloqueActivo) return b;
        const newPts = [...b.puntos] as [number,number][];
        newPts[idx] = [nx, ny];
        return { ...b, puntos: newPts };
      }));
      movedRef.current = true;
      return;
    }
    if (!panRef.current || !containerRef.current) return;
    const dx = e.clientX - panRef.current.x;
    const dy = e.clientY - panRef.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
    containerRef.current.scrollLeft = panRef.current.sl - dx;
    containerRef.current.scrollTop = panRef.current.st - dy;
  }
  function onPanEnd() {
    vertexDragRef.current = null;
    panRef.current = null; setIsPanning(false);
  }


  // Wheel zoom
  function onWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom(z => {
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      return Math.min(6, Math.max(0.4, +(z + delta).toFixed(2)));
    });
  }

  // Clic en imagen → añadir punto (excepto cuando se está editando vértices)
  function onImgClick(e: React.MouseEvent<HTMLImageElement>) {
    if (movedRef.current) { movedRef.current = false; return; }
    if (modoEditarVertices) return; // en modo editar, el clic no añade puntos
    if (!imgRef.current) return;
    e.stopPropagation();
    const rect = imgRef.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    if (modoRegla) {
      setReglaPoints(p => [...p, [nx, ny]]);
    } else {
      setPoints(p => [...p, [nx, ny]]);
    }
  }

  function addBlock() {
    if (points.length < 3) { setMotorErr('Marcá al menos 3 puntos para cerrar un bloque.'); return; }
    const num = bloqueNumEfectivo;
    if (!num || num < 1) { setMotorErr('Número de bloque inválido.'); return; }
    // Si ya existe ese num, reemplazar el polígono (redibujo)
    setBlocks(prev => {
      const sin = prev.filter(b => b.bloque_num !== num);
      return [...sin, { bloque_num: num, puntos: [...points] }];
    });
    setPoints([]);
    setBloqueNumInput(''); // resetear para que el siguiente use nextBlock automático
    setMotorErr('');
  }

  function removeBlock(num: number) {
    setBlocks(prev => prev.filter(b => b.bloque_num !== num));
    if (bloqueActivo === num) { setBloqueActivo(null); setModoEditarVertices(false); }
  }

  function simplificarBloque(num: number) {
    setBlocks(prev => prev.map(b => {
      if (b.bloque_num !== num) return b;
      // epsilon en coordenadas normalizadas: ~0.002 equivale a ~0.2% de la imagen
      const simplified = douglasPeucker(b.puntos, 0.002);
      return { ...b, puntos: simplified.length >= 3 ? simplified : b.puntos };
    }));
  }

  function pointsToSvgPoly(pts: [number, number][]) {
    return pts.map(p => `${p[0] * 100},${p[1] * 100}`).join(' ');
  }

  async function autoDetectarBloques() {
    if (!tifFile) { setMotorErr('Cargá el archivo TIF para auto-detectar bloques.'); return; }
    if (!lote.geojson) { setMotorErr('El lote no tiene perímetro GeoJSON definido.'); return; }
    setAutoDetecting(true); setMotorErr('');
    try {
      const gsdVal = Math.max(0.01, Number(gsd) || 0.05);
      const res = await invocarAutoDetectarBloques(
        tifFile,
        lote.geojson,
        lote.id || 'upload',
        gsdVal,
        1.5,
      );
      if (!res.bloques?.length) {
        setMotorErr('No se detectaron bloques. Probá ajustando el GSD o dibujá los bloques manualmente.');
        return;
      }
      // Agregar bloques detectados al canvas (reemplaza los existentes si ya hay)
      const nuevos: BloqueDraw[] = res.bloques.map(b => ({
        bloque_num: b.bloque_num,
        puntos: b.puntos_norm as [number, number][],
      }));
      setBlocks(nuevos);
      setPoints([]);
    } catch (e: any) {
      setMotorErr(e?.message || 'Error al auto-detectar bloques.');
    } finally {
      setAutoDetecting(false);
    }
  }

  async function runAnalisis() {
    if (!tifFile) { setMotorErr('Cargá el archivo TIF del dron para analizar.'); return; }
    if (blocks.length === 0) { setMotorErr('Dibujá al menos un bloque de siembra.'); return; }
    setRunning(true); setMotorErr(''); setResultado(null);
    try {
      const gsdVal = Math.max(0.01, Number(gsd) || 0.05);
      const camasVal = Math.max(1, Math.round(Number(camas) || 12));
      const res = await invocarMotorPythonBloquesPoligonoArchivo(
        { minArea: 20, maxArea: 800 },
        blocks.map(b => ({ bloque_num: b.bloque_num, puntos: b.puntos })),
        tifFile,
        null,
        lote.id || 'upload',
        { areaMode: 'fixed_gsd', gsdM: gsdVal, detectMode: 'conteo5_camas', expectedBeds: camasVal }
      );
      setResultado(res);
    } catch (e: any) {
      setMotorErr(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  async function guardarResultados() {
    if (!resultado?.bloques?.length) return;
    setSaving(true);
    try {
      // Convertir puntos lon/lat → normalizados y guardar en estado para visualización
      const bounds = lote.raster_bounds as [[number,number],[number,number]] | null;
      if (bounds) {
        const [[minLat, minLng], [maxLat, maxLng]] = bounds;
        const nuevosPuntos: Record<number, [number,number][]> = {};
        for (const b of resultado.bloques) {
          const puntosGeo = (b as any).puntos_geo as Array<{lon:number;lat:number}> | undefined;
          if (puntosGeo?.length) {
            nuevosPuntos[b.bloque_num!] = puntosGeo.map(p => [
              (p.lon - minLng) / Math.max(1e-10, maxLng - minLng),
              1 - (p.lat - minLat) / Math.max(1e-10, maxLat - minLat),
            ]);
          }
        }
        if (Object.keys(nuevosPuntos).length) {
          setPuntosPlantasPorBloque(prev => ({ ...prev, ...nuevosPuntos }));
        }
      }

      for (const b of resultado.bloques) {
        if (b.bloque_num == null) continue;
        const puntos = blocks.find(bl => bl.bloque_num === b.bloque_num)?.puntos ?? [];
        const geojsonBloque = lote.raster_bounds && puntos.length >= 3
          ? puntosToGeojson(puntos, lote.raster_bounds as [[number,number],[number,number]])
          : null;

        // Leer valores existentes para no pisar conteo calibrado
        const { data: existing } = await supabase
          .from('emp_bloques')
          .select('plant_count, area_ha, conteo_resultado_payload')
          .eq('lote_id', lote.id)
          .eq('num', b.bloque_num)
          .maybeSingle();
        const prevPayload = existing?.conteo_resultado_payload ?? {};

        const { error } = await supabase.from('emp_bloques').upsert({
          empresa_id: empresaId,
          lote_id: lote.id,
          num: b.bloque_num,
          tipo: 'siembra',
          // Solo actualizar plant_count/area_ha si no hay dato calibrado previo
          ...(existing?.plant_count == null ? { plant_count: b.conteo ?? null } : {}),
          ...(existing?.area_ha == null ? { area_ha: b.area_ha ?? null } : {}),
          conteo_detectado_at: new Date().toISOString(),
          conteo_modo: 'rgb',
          ...(geojsonBloque ? { geojson: geojsonBloque } : {}),
          conteo_resultado_payload: { ...prevPayload, ...b, puntos },
          activo: true,
        }, { onConflict: 'empresa_id,lote_id,num' });
        if (error) throw new Error(`Bloque ${b.bloque_num}: ${error.message}`);
      }

      // Actualizar área sembrada solo si no hay datos calibrados
      const { data: bloquesExist } = await supabase
        .from('emp_bloques')
        .select('area_ha')
        .eq('lote_id', lote.id);
      const areaSembrada = (bloquesExist ?? []).reduce((s, b) => s + (b.area_ha ?? 0), 0);
      if (areaSembrada > 0) {
        await supabase.from('emp_lotes')
          .update({ area_ha_sembrada: parseFloat(areaSembrada.toFixed(4)) })
          .eq('id', lote.id);
      }

      setSaved(true);
      onSaved?.();
    } catch (e: any) {
      setMotorErr(e?.message ?? 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function guardarPoligonos() {
    if (blocks.length === 0) return;
    setSaving(true);
    try {
      for (const b of blocks) {
        // Leer payload existente para no borrar datos de conteo previos
        const { data: existing } = await supabase
          .from('emp_bloques')
          .select('conteo_resultado_payload')
          .eq('lote_id', lote.id)
          .eq('num', b.bloque_num)
          .maybeSingle();
        const prevPayload = existing?.conteo_resultado_payload ?? {};
        const geojsonBloque = lote.raster_bounds
          ? puntosToGeojson(b.puntos, lote.raster_bounds as [[number,number],[number,number]])
          : null;
        const { error } = await supabase.from('emp_bloques').upsert({
          empresa_id: empresaId,
          lote_id: lote.id,
          num: b.bloque_num,
          tipo: 'siembra',
          ...(geojsonBloque ? { geojson: geojsonBloque } : {}),
          conteo_resultado_payload: { ...prevPayload, puntos: b.puntos },
          activo: true,
        }, { onConflict: 'empresa_id,lote_id,num' });
        if (error) throw new Error(`Bloque ${b.bloque_num}: ${error.message}`);
      }
      setSaved(true);
      onSaved?.();
    } catch (e: any) {
      setMotorErr(e?.message ?? 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  const totalPlantas = resultado?.bloques?.reduce((s, b) => s + (b.conteo ?? 0), 0) ?? 0;
  const totalHa = resultado?.bloques?.reduce((s, b) => s + (b.area_ha ?? 0), 0) ?? 0;
  const densidadProm = totalHa > 0 ? totalPlantas / totalHa : 0;
  const kpiProm = resultado?.bloques?.length
    ? resultado.bloques.reduce((s, b) => s + (b.kpi ?? 0), 0) / resultado.bloques.length
    : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'var(--surface-base)' }}>

      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-2.5 shrink-0"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)', minHeight: 48 }}>
        <button onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm shrink-0"
          style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
          <ArrowLeft size={14} /> Volver
        </button>

        <div className="flex-1 min-w-0 flex items-center gap-1.5 text-sm">
          <span className="font-medium truncate" style={{ color: 'var(--ink-faint)' }}>{finca.nombre}</span>
          <span style={{ color: 'var(--ink-faint)' }}>›</span>
          <span className="font-medium truncate" style={{ color: 'var(--ink-faint)' }}>Lote {lote.numero} — {lote.nombre}</span>
          <span style={{ color: 'var(--ink-faint)' }}>›</span>
          <span className="font-bold truncate" style={{ color: 'var(--ink)' }}>Bloques de Siembra</span>
        </div>

        {/* Cargar TIF original para el motor */}
        <button type="button" onClick={() => tifInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border shrink-0"
          style={{ borderColor: 'var(--tone-info-border)', color: 'var(--tone-info-text)', background: 'var(--tone-info-bg)' }}>
          <Upload size={12} />
          {tifFile ? tifFile.name.slice(0, 22) + '…' : 'Cargar TIF para el motor'}
        </button>
        <input ref={tifInputRef} type="file" accept=".tif,.tiff,.png,.jpg" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) cargarTif(f); }} />
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* Panel izquierdo: imagen */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 p-3 gap-2">

          {/* Toolbar zoom + bloque */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <div className="flex items-center gap-1 rounded-lg border px-2 py-1"
              style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <button onClick={() => setZoom(z => Math.min(6, +(z + 0.25).toFixed(2)))} className="p-1 rounded hover:opacity-70">
                <ZoomIn size={14} style={{ color: 'var(--ink-muted)' }} />
              </button>
              <span className="text-xs w-12 text-center font-mono" style={{ color: 'var(--ink-faint)' }}>
                {(zoom * 100).toFixed(0)}%
              </span>
              <button onClick={() => setZoom(z => Math.max(0.4, +(z - 0.25).toFixed(2)))} className="p-1 rounded hover:opacity-70">
                <ZoomOut size={14} style={{ color: 'var(--ink-muted)' }} />
              </button>
              <button onClick={() => { setZoom(1); if (containerRef.current) { containerRef.current.scrollLeft = 0; containerRef.current.scrollTop = 0; } }}
                className="p-1 rounded hover:opacity-70">
                <RotateCcw size={13} style={{ color: 'var(--ink-muted)' }} />
              </button>
            </div>

            <div className="flex items-center gap-1 rounded-lg border px-2 py-1"
              style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <span className="text-xs font-mono" style={{ color: 'var(--ink-faint)' }}>
                {points.length} pts
              </span>
              <button
                onClick={() => setPoints(p => p.slice(0, -1))}
                disabled={points.length === 0}
                className="text-xs px-2 py-0.5 rounded ml-1"
                style={{ background: 'var(--surface-overlay)', color: points.length > 0 ? 'var(--ink-muted)' : 'var(--ink-faint)', border: '1px solid var(--line)', opacity: points.length === 0 ? 0.4 : 1 }}>
                ← Deshacer
              </button>
              <button
                onClick={() => setPoints([])}
                disabled={points.length === 0}
                className="text-xs px-2 py-0.5 rounded"
                style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)', opacity: points.length === 0 ? 0.4 : 1 }}>
                Limpiar
              </button>
              {points.length >= 3 && (
                <>
                  <input
                    type="number" min="1"
                    value={bloqueNumInput}
                    onChange={e => setBloqueNumInput(e.target.value)}
                    placeholder={String(nextBlock)}
                    title="Número de bloque (vacío = siguiente automático)"
                    className="w-12 px-1.5 py-0.5 rounded text-xs text-center font-mono ml-1"
                    style={{ background: 'var(--surface-overlay)', border: '1px solid var(--emp-accent)', color: 'var(--emp-accent-txt)' }}
                  />
                  <button onClick={addBlock}
                    className="text-xs px-2 py-0.5 rounded font-semibold"
                    style={{ background: 'var(--emp-accent)', color: '#fff' }}>
                    ✓ Cerrar B{bloqueNumEfectivo}
                  </button>
                </>
              )}
            </div>

            {/* Toggle puntos plantas */}
            {Object.keys(puntosPlantasPorBloque).length > 0 && (
              <button
                onClick={() => setShowPuntos(s => !s)}
                className="text-xs px-2.5 py-1 rounded-lg border"
                style={{
                  background: showPuntos ? 'rgba(34,197,94,0.15)' : 'var(--surface-raised)',
                  borderColor: showPuntos ? '#22c55e' : 'var(--line)',
                  color: showPuntos ? '#4ade80' : 'var(--ink-faint)',
                }}>
                🌿 Plantas
              </button>
            )}

            {/* Modo Regla */}
            <button
              onClick={() => { setModoRegla(m => !m); setReglaPoints([]); }}
              className="text-xs px-2.5 py-1 rounded-lg border ml-auto"
              style={{
                background: modoRegla ? 'var(--tone-info-bg)' : 'var(--surface-raised)',
                borderColor: modoRegla ? 'var(--tone-info-border)' : 'var(--line)',
                color: modoRegla ? 'var(--tone-info-text)' : 'var(--ink-faint)',
                fontWeight: modoRegla ? 600 : 400,
              }}>
              📏 {modoRegla ? 'Regla ON' : 'Regla'}
            </button>
            {modoRegla && reglaPoints.length > 0 && (
              <button onClick={() => setReglaPoints([])} className="text-xs px-2 py-1 rounded border"
                style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)', background: 'var(--surface-raised)' }}>
                Limpiar regla
              </button>
            )}
            {modoRegla && reglaPoints.length >= 2 && (
              <div className="flex items-center gap-1 rounded-lg border px-2 py-1"
                style={{ borderColor: 'var(--tone-info-border)', background: 'var(--tone-info-bg)' }}>
                <span className="text-[10px]" style={{ color: 'var(--tone-info-text)' }}>Distancia real (m):</span>
                <input
                  type="number" step="0.1" min="0.1"
                  value={reglaDistReal}
                  onChange={e => setReglaDistReal(e.target.value)}
                  placeholder="ej. 12.6"
                  className="w-16 px-1.5 py-0.5 rounded text-xs text-center"
                  style={{ background: 'var(--surface-overlay)', border: '1px solid var(--tone-info-border)', color: 'var(--ink)' }}
                />
                <button
                  onClick={() => {
                    const real = parseFloat(reglaDistReal);
                    if (!real || !imgDims) return;
                    // distancia en píxeles de la línea total
                    let px = 0;
                    for (let i = 1; i < reglaPoints.length; i++) {
                      const [ax, ay] = reglaPoints[i-1];
                      const [bx, by] = reglaPoints[i];
                      const dx = (bx - ax) * imgDims.w;
                      const dy = (by - ay) * imgDims.h;
                      px += Math.sqrt(dx*dx + dy*dy);
                    }
                    if (px > 0) setGsd((real / px).toFixed(4));
                  }}
                  className="text-[10px] px-2 py-0.5 rounded font-semibold"
                  style={{ background: 'var(--tone-info-text)', color: '#fff' }}>
                  Calibrar GSD
                </button>
              </div>
            )}
            {!modoRegla && !modoEditarVertices && (
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                Clic: marcar · Backspace: deshacer · Ctrl+scroll: zoom
              </span>
            )}
            {modoEditarVertices && (
              <span className="text-xs font-medium" style={{ color: '#a78bfa' }}>
                ✏️ Arrastrá los puntos morados para mover vértices · clic fuera del polígono no añade puntos
              </span>
            )}
          </div>

          {/* Mapa satélite de referencia (cuando no hay TIF) */}
          {!previewUrl && !loadingImg && lote.geojson && (
            <div className="flex-1 min-h-0 rounded-xl overflow-hidden" style={{ border: '1px solid var(--line)', minHeight: 200 }}>
              <GeoMapPreview
                geojson={lote.geojson}
                height="100%"
                maxWidthClassName="max-w-full"
                polygonColor="#38bdf8"
                editable={false}
              />
            </div>
          )}

          {/* Placeholder sin imagen */}
          {!previewUrl && !loadingImg && !lote.geojson && (
            <div className="flex-1 min-h-0 rounded-xl flex flex-col items-center justify-center gap-3"
              style={{ border: '1px dashed var(--line)', background: 'var(--surface-raised)' }}>
              <ImagePlus size={48} style={{ color: 'var(--ink-faint)', opacity: 0.3 }} />
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
                Cargá el TIF del dron para comenzar a dibujar los bloques
              </p>
              {imgErr && (
                <p className="text-xs px-3 py-1 rounded"
                  style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                  {imgErr}
                </p>
              )}
            </div>
          )}

          {/* Loading TIF */}
          {loadingImg && (
            <div className="flex-1 min-h-0 rounded-xl flex items-center justify-center gap-3"
              style={{ border: '1px solid var(--line)', background: '#0a0f1a' }}>
              <Loader2 size={32} className="animate-spin" style={{ color: 'var(--emp-accent)' }} />
              <span className="text-sm" style={{ color: 'var(--ink-faint)' }}>Procesando imagen...</span>
            </div>
          )}

          {/* Canvas con imagen + overlay SVG */}
          {previewUrl && !loadingImg && (
            <div
              ref={containerRef}
              className="flex-1 min-h-0 rounded-xl overflow-auto"
              style={{ border: '1px solid var(--line)', background: '#0a0f1a', cursor: modoEditarVertices ? (vertexDragRef.current ? 'grabbing' : 'crosshair') : isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'crosshair' }}
              onWheel={onWheel}
              onMouseDown={onPanStart}
              onMouseMove={onPanMove}
              onMouseUp={onPanEnd}
              onMouseLeave={onPanEnd}
            >
              <div className="flex items-start justify-start min-h-full">
                <div
                  className="relative inline-block"
                  style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
                >
                  <img
                    ref={imgRef}
                    src={previewUrl}
                    alt="ortofoto"
                    className="block select-none"
                    style={{ maxWidth: 'none', cursor: modoEditarVertices ? 'default' : isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'crosshair' }}
                    draggable={false}
                    onClick={onImgClick}
                    onLoad={e => {
                      const img = e.currentTarget;
                      setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
                    }}
                  />
                  <svg
                    className="absolute inset-0 w-full h-full"
                    style={{ pointerEvents: 'none' }}
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {/* Bloques cerrados */}
                    {blocks.map(b => {
                      const resaltado = bloqueActivo === b.bloque_num;
                      const editando = resaltado && modoEditarVertices;
                      const cx = b.puntos.reduce((s, p) => s + p[0], 0) / b.puntos.length * 100;
                      const cy = b.puntos.reduce((s, p) => s + p[1], 0) / b.puntos.length * 100;
                      const areaM2 = resaltado ? calcAreaM2(b.puntos) : null;
                      return (
                        <g key={b.bloque_num}>
                          <polygon
                            points={pointsToSvgPoly(b.puntos)}
                            fill={editando ? 'rgba(139,92,246,0.2)' : resaltado ? 'rgba(251,146,60,0.35)' : 'rgba(16,163,74,0.1)'}
                            stroke={editando ? '#8b5cf6' : resaltado ? '#f97316' : '#16a34a'}
                            strokeWidth={resaltado ? 0.2 : 0.08}
                          />
                          <text x={cx} y={cy - (resaltado && areaM2 ? 2 : 0)}
                            fontSize="2.5" fill={editando ? '#a78bfa' : resaltado ? '#fb923c' : '#4ade80'}
                            textAnchor="middle" dominantBaseline="middle"
                            fontWeight="bold" stroke="#000" strokeWidth="0.2" paintOrder="stroke">
                            B{b.bloque_num}
                          </text>
                          {resaltado && areaM2 != null && (
                            <text x={cx} y={cy + 3.5}
                              fontSize="2" fill="#fff"
                              textAnchor="middle" dominantBaseline="middle"
                              stroke="#000" strokeWidth="0.3" paintOrder="stroke">
                              {areaM2.toLocaleString('es-CR')} m²
                            </text>
                          )}
                          {/* Vértices visibles en modo editar */}
                          {editando && b.puntos.map((p, i) => (
                            <circle
                              key={i}
                              cx={p[0] * 100} cy={p[1] * 100}
                              r="0.7"
                              fill="#8b5cf6"
                              stroke="#fff"
                              strokeWidth="0.15"
                            />
                          ))}
                        </g>
                      );
                    })}
                    {/* Puntos de plantas detectadas */}
                    {showPuntos && Object.entries(puntosPlantasPorBloque).map(([num, pts]) => {
                      const activo = bloqueActivo === Number(num);
                      return pts.map((p, i) => (
                        <circle key={`p${num}-${i}`}
                          cx={p[0] * 100} cy={p[1] * 100}
                          r="0.35"
                          fill={activo ? '#4ade80' : '#22c55e'}
                          opacity={activo ? 0.9 : 0.7}
                        />
                      ));
                    })}

                    {/* Trazo en progreso */}
                    {points.length > 1 && (
                      <polyline
                        points={points.map(p => `${p[0] * 100},${p[1] * 100}`).join(' ')}
                        fill="none" stroke="#ef4444" strokeWidth="0.12"
                      />
                    )}
                    {/* Puntos activos */}
                    {points.map((p, i) => (
                      <circle key={i} cx={p[0] * 100} cy={p[1] * 100} r="0.18" fill="#ef4444" />
                    ))}
                    {/* Línea de cierre (preview) */}
                    {points.length >= 3 && (
                      <line
                        x1={points[points.length - 1][0] * 100} y1={points[points.length - 1][1] * 100}
                        x2={points[0][0] * 100} y2={points[0][1] * 100}
                        stroke="#ef4444" strokeWidth="0.1" strokeDasharray="1.5,0.8"
                      />
                    )}

                    {/* ── Regla ── */}
                    {(() => {
                      const z = Math.max(0.1, zoom);
                      const sw = 0.15 / z;
                      const fs = 1.6 / z;
                      const fsBold = 1.8 / z;
                      const r = 0.25 / z;
                      const strokeTxt = 0.2 / z;
                      const strokeBold = 0.25 / z;
                      const off = 0.8 / z;
                      const offBold = 1.5 / z;
                      return (
                        <>
                          {reglaPoints.length > 1 && reglaPoints.map((p, i) => {
                            if (i === 0) return null;
                            const prev = reglaPoints[i - 1];
                            const mx = (prev[0] + p[0]) / 2 * 100;
                            const my = (prev[1] + p[1]) / 2 * 100;
                            const dist = reglaDistM(prev, p);
                            const label = dist >= 1000 ? `${(dist/1000).toFixed(2)} km` : `${dist.toFixed(1)} m`;
                            return (
                              <g key={i}>
                                <line
                                  x1={prev[0]*100} y1={prev[1]*100}
                                  x2={p[0]*100} y2={p[1]*100}
                                  stroke="#38bdf8" strokeWidth={sw}
                                />
                                <text x={mx} y={my - off} fontSize={fs} fill="#38bdf8"
                                  textAnchor="middle" dominantBaseline="middle"
                                  stroke="#000" strokeWidth={strokeTxt} paintOrder="stroke">
                                  {label}
                                </text>
                              </g>
                            );
                          })}
                          {reglaPoints.map((p, i) => (
                            <circle key={i} cx={p[0]*100} cy={p[1]*100} r={r} fill="#38bdf8" stroke="#fff" strokeWidth={sw * 0.5} />
                          ))}
                          {reglaPoints.length > 1 && (
                            <text
                              x={reglaPoints[reglaPoints.length-1][0]*100}
                              y={reglaPoints[reglaPoints.length-1][1]*100 - offBold}
                              fontSize={fsBold} fill="#fff" textAnchor="middle"
                              stroke="#000" strokeWidth={strokeBold} paintOrder="stroke" fontWeight="bold">
                              Total: {reglaTotalM() >= 1000 ? `${(reglaTotalM()/1000).toFixed(2)} km` : `${reglaTotalM().toFixed(1)} m`}
                            </text>
                          )}
                        </>
                      );
                    })()}
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Panel derecho ── */}
        <div className="w-80 shrink-0 flex flex-col min-h-0 border-l overflow-y-auto"
          style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>

          {/* Parámetros motor */}
          <div className="p-4 space-y-3 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
              Parámetros Motor V5
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] mb-1 block" style={{ color: 'var(--ink-faint)' }}>GSD (m/px)</label>
                <input className={inputCls} type="number" step="0.01" min="0.01" value={gsd}
                  onChange={e => setGsd(e.target.value)} />
              </div>
              <div>
                <label className="text-[11px] mb-1 block" style={{ color: 'var(--ink-faint)' }}>Camas esperadas</label>
                <input className={inputCls} type="number" step="1" min="1" value={camas}
                  onChange={e => setCamas(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Bloques dibujados */}
          <div className="p-4 space-y-2 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
              <Layers size={11} className="inline mr-1" />
              Bloques ({blocks.length})
            </p>
            {blocks.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                Hacé clic en la imagen para marcar vértices
              </p>
            ) : (
              blocks.map(b => {
                const activo = bloqueActivo === b.bloque_num;
                const editandoEste = activo && modoEditarVertices;
                return (
                  <div key={b.bloque_num} className="rounded overflow-hidden"
                    style={{ border: activo ? (editandoEste ? '1px solid #8b5cf6' : '1px solid #f97316') : '1px solid var(--line-dim)' }}>
                    <div
                      className="flex items-center justify-between px-2 py-1.5 cursor-pointer"
                      onClick={() => {
                        if (activo) { setBloqueActivo(null); setModoEditarVertices(false); }
                        else { setBloqueActivo(b.bloque_num); setModoEditarVertices(false); }
                      }}
                      style={{ background: activo ? (editandoEste ? 'rgba(139,92,246,0.15)' : 'rgba(251,146,60,0.15)') : 'var(--surface-overlay)' }}>
                      <span className="text-xs font-semibold" style={{ color: activo ? (editandoEste ? '#a78bfa' : '#f97316') : 'var(--emp-accent-txt)' }}>
                        Bloque B{b.bloque_num}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{b.puntos.length} pts</span>
                      <button onClick={e => { e.stopPropagation(); removeBlock(b.bloque_num); }} className="p-1"
                        style={{ color: 'var(--ink-faint)' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                        <X size={12} />
                      </button>
                    </div>
                    {/* Acciones del bloque activo */}
                    {activo && (
                      <div className="flex gap-1 px-2 py-1.5 flex-wrap"
                        style={{ background: 'var(--surface-deep)', borderTop: '1px solid var(--line-dim)' }}>
                        <button
                          onClick={() => setModoEditarVertices(m => !m)}
                          className="text-[10px] px-2 py-0.5 rounded font-medium"
                          style={{
                            background: editandoEste ? '#8b5cf6' : 'var(--surface-overlay)',
                            color: editandoEste ? '#fff' : 'var(--ink-muted)',
                            border: `1px solid ${editandoEste ? '#8b5cf6' : 'var(--line)'}`,
                          }}>
                          {editandoEste ? '✓ Editando vértices' : '✏️ Editar vértices'}
                        </button>
                        {b.puntos.length > 10 && (
                          <button
                            onClick={e => { e.stopPropagation(); simplificarBloque(b.bloque_num); }}
                            className="text-[10px] px-2 py-0.5 rounded font-medium"
                            style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}
                            title={`Reducir ${b.puntos.length} puntos con Douglas-Peucker`}>
                            ⚡ Simplificar ({b.puntos.length}→?)
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); setPoints([]); setBloqueNumInput(String(b.bloque_num)); removeBlock(b.bloque_num); }}
                          className="text-[10px] px-2 py-0.5 rounded font-medium"
                          style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                          🔄 Redibujar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
            {blocks.length > 0 && (
              <div className="flex gap-1.5 mt-1">
                <button
                  onClick={guardarPoligonos}
                  disabled={saving}
                  className="flex-1 text-[11px] px-2 py-1.5 rounded font-semibold inline-flex items-center justify-center gap-1"
                  style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)', opacity: saving ? 0.6 : 1 }}>
                  {saving ? <><Loader2 size={11} className="animate-spin" /> Guardando...</> : <><Download size={11} /> Guardar polígonos</>}
                </button>
                <button
                  onClick={() => { setBlocks([]); setPoints([]); setResultado(null); setSaved(false); }}
                  className="text-[11px] px-2 py-1.5 rounded"
                  style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                  Limpiar
                </button>
              </div>
            )}
          </div>

          {/* Botón lanzar motor */}
          <div className="p-4 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            {motorErr && (
              <div className="flex gap-2 p-2 rounded mb-3"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
                <AlertCircle size={13} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: 'var(--tone-warn-ink)' }}>{motorErr}</p>
              </div>
            )}
            {imgErr && (
              <div className="flex gap-2 p-2 rounded mb-3"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
                <AlertCircle size={13} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: 'var(--tone-warn-ink)' }}>{imgErr}</p>
              </div>
            )}
            <button
              onClick={runAnalisis}
              disabled={running || blocks.length === 0}
              className="w-full py-2.5 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition-opacity"
              style={{ background: 'var(--emp-accent)', color: '#fff', opacity: running || blocks.length === 0 ? 0.5 : 1 }}>
              {running
                ? <><Loader2 size={15} className="animate-spin" /> Analizando...</>
                : <><FlaskConical size={15} /> Lanzar Motor V5</>}
            </button>
            {!tifFile && blocks.length > 0 && (
              <p className="text-[10px] mt-1.5 text-center" style={{ color: 'var(--ink-faint)' }}>
                Necesitás cargar el TIF original para lanzar el motor
              </p>
            )}
          </div>

          {/* Resultados */}
          {resultado && (
            <div className="flex-1 p-4 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Total plantas', value: fmtNum(totalPlantas), accent: true },
                  { label: 'Área analizada', value: `${fmtNum(totalHa, 2)} ha` },
                  { label: 'Densidad prom.', value: `${fmtNum(densidadProm, 0)} pl/ha` },
                  { label: 'KPI promedio', value: `${fmtNum(kpiProm, 1)}%`, kpi: kpiProm },
                ].map(item => (
                  <div key={item.label} className="rounded-lg p-2.5"
                    style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}>
                    <p className="text-[10px] mb-0.5" style={{ color: 'var(--ink-faint)' }}>{item.label}</p>
                    <p className="text-sm font-bold"
                      style={{ color: item.kpi != null ? kpiColor(item.kpi) : item.accent ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Tabla por bloque */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--ink-faint)' }}>
                  Detalle por Bloque
                </p>
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line)' }}>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr style={{ background: 'var(--surface-deep)' }}>
                        {['Bloque', 'Plantas', 'Área', 'Densidad', 'KPI'].map(h => (
                          <th key={h} className="px-2 py-1.5 text-left font-semibold"
                            style={{ color: 'var(--ink-faint)', borderBottom: '1px solid var(--line)' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.bloques?.map((b, i) => (
                        <tr key={b.bloque_num ?? i}
                          style={{ background: i % 2 === 0 ? 'var(--surface-raised)' : 'var(--surface-overlay)' }}>
                          <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>
                            B{b.bloque_num}
                          </td>
                          <td className="px-2 py-1.5 font-semibold" style={{ color: 'var(--ink)' }}>
                            {fmtNum(b.conteo)}
                          </td>
                          <td className="px-2 py-1.5" style={{ color: 'var(--ink-muted)' }}>
                            {fmtNum(b.area_ha, 2)} ha
                          </td>
                          <td className="px-2 py-1.5" style={{ color: 'var(--ink-muted)' }}>
                            {fmtNum(b.densidad, 0)}
                          </td>
                          <td className="px-2 py-1.5 font-semibold" style={{ color: kpiColor(b.kpi) }}>
                            {b.kpi != null ? `${fmtNum(b.kpi, 1)}%` : '—'}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--surface-deep)', borderTop: '2px solid var(--line)' }}>
                        <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--ink)' }}>Total</td>
                        <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>
                          {fmtNum(totalPlantas)}
                        </td>
                        <td className="px-2 py-1.5 font-semibold" style={{ color: 'var(--ink)' }}>
                          {fmtNum(totalHa, 2)} ha
                        </td>
                        <td className="px-2 py-1.5 font-semibold" style={{ color: 'var(--ink)' }}>
                          {fmtNum(densidadProm, 0)}
                        </td>
                        <td className="px-2 py-1.5 font-bold" style={{ color: kpiColor(kpiProm) }}>
                          {fmtNum(kpiProm, 1)}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Guardar */}
              {saved ? (
                <div className="flex items-center gap-2 p-2.5 rounded-lg"
                  style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)' }}>
                  <CheckCircle2 size={15} style={{ color: 'var(--tone-success-text)' }} />
                  <p className="text-xs font-medium" style={{ color: 'var(--tone-success-text)' }}>
                    Resultados guardados en los bloques del lote
                  </p>
                </div>
              ) : (
                <button onClick={guardarResultados} disabled={saving}
                  className="w-full py-2 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2"
                  style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)', opacity: saving ? 0.6 : 1 }}>
                  {saving
                    ? <><Loader2 size={14} className="animate-spin" /> Guardando...</>
                    : <><Download size={14} /> Guardar en bloques del lote</>}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Portal wrapper
export function BloqueWorkspace(props: BloqueWorkspaceProps) {
  return ReactDOM.createPortal(<Workspace {...props} />, document.body);
}
